// mockSurveySeedChecklists.js
// ─────────────────────────────────────────────────────────────────────────────
// Hand-authored, clinically-grounded self-audit checklists for the highest-
// frequency F-tags, structured to mirror CMS Critical Element Pathway logic
// (Observation → Record Review → Interview → Critical Element). These ship in
// the app as an AUTHORITATIVE, always-available source used AFTER any ingested
// verbatim CMS pathway but BEFORE AI generation — so the most-cited tags are
// accurate, instant, and identical for every facility with no live-AI
// dependency. Reviewed against CMS State Operations Manual Appendix PP intent;
// facilities should still validate against their current survey guidance.
// ─────────────────────────────────────────────────────────────────────────────

const SEED = {
  F550: {
    "Observation": [
      "During your walk, is each resident treated with dignity — addressed by preferred name, not talked over, and not left exposed during care or transport?",
      "Are residents' personal care needs (grooming, clothing, positioning) attended so no one sits soiled, in a gown in common areas, or unkempt?",
    ],
    "Interview": [
      "Ask residents/representatives: do staff respect your choices, privacy, and schedule (waking, bathing, activities)?",
    ],
    "Record Review": [
      "Do care plans reflect resident preferences and choices (rise/bed times, bathing type, activities), and is honoring them documented?",
    ],
    "Critical Element": [
      "Did the facility promote and protect each resident's right to be treated with dignity and to exercise choice? Mark non-compliant if dignity/choice was not honored.",
    ],
  },
  F600: {
    "Observation": [
      "Do you observe any rough handling, dismissive/demeaning speech, ignoring of requests, or resident-to-resident aggression not being intervened on?",
    ],
    "Record Review": [
      "For any injury of unknown origin, altercation, or allegation: is there a report and evidence the resident was protected immediately?",
      "Do records show interventions for residents with behaviors that put self or others at risk (not just PRN medication)?",
    ],
    "Interview": [
      "Ask staff: what is abuse/neglect, and exactly what do you do and who do you notify if you witness or suspect it?",
    ],
    "Critical Element": [
      "Was each resident free from abuse, neglect, and exploitation, and did the facility protect residents from harm? Mark non-compliant if any resident was not protected.",
    ],
  },
  F609: {
    "Record Review": [
      "For every alleged abuse/neglect/misappropriation or injury of unknown origin, was the administrator and required state agencies notified within the required timeframe (2 hours if serious bodily injury, 24 hours otherwise)?",
      "Is there a log/record showing WHO reported, WHEN, and to WHICH agencies?",
    ],
    "Interview": [
      "Ask the administrator/DON: walk me through your last reportable event — when and to whom was it reported?",
    ],
    "Critical Element": [
      "Did the facility report alleged violations to the administrator and officials within required timeframes? Mark non-compliant if any report was late or missing.",
    ],
  },
  F610: {
    "Record Review": [
      "For each allegation: is there a thorough investigation (statements, evidence, timeline) completed and results reported within 5 working days?",
      "Were interim protections put in place during the investigation (e.g., accused staff removed from resident contact)?",
      "Were corrective actions implemented to prevent recurrence?",
    ],
    "Critical Element": [
      "Did the facility investigate thoroughly, protect residents during the investigation, and act to prevent recurrence? Mark non-compliant if any element is missing.",
    ],
  },
  F636: {
    "Record Review": [
      "Is a comprehensive MDS assessment completed within 14 days of admission, annually, and within 14 days of a significant change?",
      "Does the assessment accurately reflect the resident's actual status (compare MDS coding to the chart and your observation)?",
    ],
    "Interview": [
      "Ask the MDS coordinator: how do you verify coding accuracy against source documentation?",
    ],
    "Critical Element": [
      "Was a timely, accurate comprehensive assessment completed? Mark non-compliant if late, missing, or not matching the resident's status.",
    ],
  },
  F656: {
    "Record Review": [
      "Does each resident have a person-centered comprehensive care plan developed within 7 days of the comprehensive assessment?",
      "Are measurable goals and specific interventions present for each identified problem/risk, developed by the interdisciplinary team (including the resident/representative)?",
    ],
    "Observation": [
      "Are the care plan's interventions actually being delivered (compare the plan to what you observe at the bedside)?",
    ],
    "Critical Element": [
      "Did the facility develop and implement a comprehensive person-centered care plan? Mark non-compliant if a needed intervention is missing or not implemented.",
    ],
  },
  F657: {
    "Record Review": [
      "Is the care plan reviewed and revised by the IDT after each assessment and whenever the resident's condition or a new risk changes?",
      "When an intervention isn't working (e.g., repeat falls, weight loss), is there evidence the plan was revised?",
    ],
    "Critical Element": [
      "Did the facility review and revise the care plan to reflect the resident's current needs? Mark non-compliant if the plan is stale despite a change.",
    ],
  },
  F677: {
    "Observation": [
      "Are dependent residents provided needed ADL care — clean, groomed, dressed, nails/oral care done, repositioned, and toileted per plan?",
    ],
    "Record Review": [
      "Do ADL flow sheets show care was actually provided at the planned frequency (not gaps or blanks)?",
    ],
    "Interview": [
      "Ask a dependent resident/family: do you get help with bathing, toileting, and grooming when you need it?",
    ],
    "Critical Element": [
      "Did residents unable to perform ADLs receive necessary services to maintain hygiene, grooming, and function? Mark non-compliant if care was not provided.",
    ],
  },
  F684: {
    "Record Review": [
      "For each active clinical condition (diabetes, wounds, respiratory, post-fall, change in condition), is monitoring, treatment, and physician notification documented and per orders?",
      "Are abnormal findings (vitals, labs, blood sugars, pain) acted upon and followed up?",
    ],
    "Observation": [
      "Is treatment/care being delivered as ordered (e.g., oxygen at ordered rate, treatments done, positioning)?",
    ],
    "Critical Element": [
      "Did the resident receive care and services consistent with professional standards to meet their needs? Mark non-compliant if care was not provided or standards not met.",
    ],
  },
  F686: {
    "Observation": [
      "For residents at risk or with pressure injuries: are repositioning, offloading, and pressure-relieving devices in use as care-planned during your observation?",
    ],
    "Record Review": [
      "Is a skin/pressure-ulcer risk assessment done on admission and with change, with individualized prevention interventions?",
      "For existing wounds: are weekly measurements, staging, treatment per order, and healing progress documented (and provider notified if worsening)?",
    ],
    "Critical Element": [
      "Did the facility prevent avoidable pressure injuries and provide care to promote healing of existing ones? Mark non-compliant if prevention or treatment gaps exist.",
    ],
  },
  F689: {
    "Record Review": [
      "Is a fall/accident-hazard risk assessment completed on admission, quarterly, after every fall, and on significant change?",
      "Does the care plan list individualized interventions (not generic) for each risk, with dates, and were they revised after any fall?",
    ],
    "Observation": [
      "Walk the unit: call lights in reach, floors dry, pathways clear, chemicals/sharps secured, safe water temperature, and equipment (wheelchairs, lifts, alarms) functional?",
      "Are at-risk residents supervised per plan (line of sight, checks, 1:1) and ordered devices (alarms, low beds, mats, wander guards) in place and working?",
    ],
    "Interview": [
      "Ask staff: what are this resident's fall interventions, and how do you confirm they're in place each shift?",
    ],
    "Critical Element": [
      "Did the facility provide adequate supervision and assistive devices to prevent avoidable accidents/elopement? Mark non-compliant if any at-risk resident lacked an implemented intervention.",
    ],
  },
  F690: {
    "Record Review": [
      "For residents with a urinary catheter: is there documented medical justification for continued use and a plan to discontinue when possible?",
      "For incontinence: is there an assessment, a toileting/continence program, and evidence it's followed?",
    ],
    "Observation": [
      "Is catheter care provided, tubing secured and below bladder, bag off the floor, and peri/incontinence care timely (no resident left soiled)?",
    ],
    "Critical Element": [
      "Did the facility provide appropriate catheter/continence care and avoid unnecessary catheter use and UTIs? Mark non-compliant if care or justification is lacking.",
    ],
  },
  F692: {
    "Record Review": [
      "Is nutritional status assessed (weights on schedule, intake tracked), and are significant weight changes (≥5% in 30 days / 10% in 180) identified and acted on?",
      "Are ordered diets, supplements, and hydration interventions documented as provided, with dietitian involvement?",
    ],
    "Observation": [
      "At a meal: are residents served the correct diet/texture, assisted/set up as needed, trays in reach, and hydration offered?",
    ],
    "Critical Element": [
      "Did the facility maintain acceptable nutrition and hydration and act on decline? Mark non-compliant if weight loss/dehydration risk was not addressed.",
    ],
  },
  F725: {
    "Observation": [
      "During your walk on this shift/unit: are call lights answered timely and residents' care needs met, or are there signs of insufficient staff (delays, missed care)?",
    ],
    "Record Review": [
      "Do staffing schedules/assignments provide sufficient nursing staff for the acuity/census, and does the posted staffing match actual?",
    ],
    "Interview": [
      "Ask staff and residents: is there enough staff to answer lights and provide timely care each shift?",
    ],
    "Critical Element": [
      "Did the facility provide sufficient nursing staff to meet residents' needs? Mark non-compliant if care was delayed/missed due to staffing.",
    ],
  },
  F758: {
    "Record Review": [
      "For each psychotropic: is there a documented diagnosis/indication, target behaviors, monitoring, and non-pharmacological interventions tried?",
      "Are PRN psychotropics limited to 14 days unless justified, and is a gradual dose reduction (GDR) attempted/documented unless clinically contraindicated?",
    ],
    "Observation": [
      "Do you observe residents who appear over-sedated, or behaviors being managed with medication rather than approaches?",
    ],
    "Critical Element": [
      "Were psychotropic medications used only when clinically indicated, monitored, and subject to GDR? Mark non-compliant if used as a chemical restraint or without indication.",
    ],
  },
  F759: {
    "Observation": [
      "Observe a medication pass: is the facility medication error rate under 5% (errors observed ÷ opportunities), including wrong time, dose, technique, or omission?",
    ],
    "Record Review": [
      "Are medication errors tracked, analyzed for cause, and reported to QAPI?",
    ],
    "Critical Element": [
      "Was the facility free of a medication error rate of 5% or greater? Mark non-compliant if the observed rate meets/exceeds 5%.",
    ],
  },
  F760: {
    "Record Review": [
      "Were there any significant medication errors (wrong resident, wrong high-alert drug, or an error causing/likely to cause harm)?",
      "For any significant error, is there root-cause analysis and corrective action?",
    ],
    "Critical Element": [
      "Was each resident free of any significant medication error? Mark non-compliant if a significant error occurred.",
    ],
  },
  F761: {
    "Observation": [
      "Check med rooms/carts/refrigerators: are drugs stored at correct temperature, secured/locked, and separated (external vs internal, per resident)?",
      "Are all medications properly labeled (resident, drug, strength, expiration) with no expired, discontinued, or unlabeled drugs present?",
      "Are controlled substances double-locked with an accurate reconciliation/count?",
    ],
    "Record Review": [
      "Are refrigerator/med-room temperature logs maintained and within range, and expired-medication checks documented?",
    ],
    "Critical Element": [
      "Were drugs and biologicals labeled and stored properly and securely? Mark non-compliant if storage, labeling, expiration, or security is deficient.",
    ],
  },
  F812: {
    "Observation": [
      "In the kitchen: are hand hygiene, hair restraints, glove use, and no bare-hand contact with ready-to-eat food observed?",
      "Are food temperatures safe (hot ≥135°F, cold ≤41°F), and are cooking/holding/cooling temps logged?",
      "Is the kitchen clean and sanitary — equipment, surfaces, dishmachine sanitizing, no pests, and food dated/covered/off the floor with no expired items?",
    ],
    "Critical Element": [
      "Did the facility procure, store, prepare, and serve food under sanitary conditions? Mark non-compliant if any food-safety practice is deficient.",
    ],
  },
  F880: {
    "Observation": [
      "Observe care: hand hygiene before/after contact, correct PPE use, proper glove changes, and appropriate transmission-based precautions for residents on isolation?",
      "Is equipment cleaned/disinfected between residents and are sharps/linens/waste handled correctly?",
    ],
    "Record Review": [
      "Is there an active infection prevention & control program with surveillance, an infection preventionist, and evidence of acting on trends/outbreaks?",
    ],
    "Interview": [
      "Ask staff: when do you perform hand hygiene, and what precautions apply to this resident?",
    ],
    "Critical Element": [
      "Did the facility maintain an infection prevention program and follow standard/transmission-based precautions? Mark non-compliant if practices lapse.",
    ],
  },
};

// Section metadata → the fields the Mock Survey renderer expects.
const SECTION_TYPE = {
  "Observation": "observations",
  "Record Review": "record_review",
  "Interview": "interview",
  "Critical Element": "critical_elements",
};

// Return a ready-to-use checklist (array of item objects) for a tag, or null if
// there's no seeded checklist. `cfr` is optional (added as cfr_reference).
export function getSeedChecklist(tag, cfr) {
  const def = SEED[tag];
  if (!def) return null;
  const items = [];
  let n = 0;
  Object.keys(def).forEach((sectionTitle) => {
    const type = SECTION_TYPE[sectionTitle] || "record_review";
    (def[sectionTitle] || []).forEach((text) => {
      n += 1;
      items.push({
        id: `${tag}-seed-${n}`,
        text,
        surveyor_focus: sectionTitle,
        cfr_reference: cfr || "",
        section_title: sectionTitle,
        section_type: type,
        source: "seed-verified",
        tag_type: "F",
      });
    });
  });
  return items.length ? items : null;
}

export function hasSeedChecklist(tag) {
  return !!SEED[tag];
}

export const SEEDED_TAGS = Object.keys(SEED);
